<template>
  <tile class="opc-zmone">
    <foldable v-if="showFoldable" v-model="isExpanded" :title="title" :arrow="canExpand" :edit="canExpand" :isRemoveWrapAriaLabelledby="true">
      <div slot="head">
        <zm-alert v-if="alertTitle" class="opc-common-info-alert" :title="alertTitle" type="warning" :closable="false"
          show-icon />
        <div class="opc-zm-base-card__head">
          <div class="opc-zm-base-card__head-left">
            <img class="opc-zm-base-card__logo" :src="logo" :alt="title" />
          </div>
          <div class="opc-zm-base-card__head-right">
            <h1 class="opc-zm-base-card__name text-capitalize">
            {{ title }}
            <i class="zm-icon-success main-icon" aria-hidden="true"></i>
          </h1>
          <div class="opc-zm-base-card__view-detail-button">
            <template v-if="isExpanded">
              <zm-button class="opc-zm-base-card__link" type="link" @click="handlePlanDetailsClick" v-if="showPlanDetails">
                {{ $t('billing.plan_details') }}
              </zm-button>
              <zm-button class="opc-zmone__link" type="link" @click="handleExploreOtherPlansClick" v-else>
                {{ $t('billing.zone.explore_other_plans_edit') }}
              </zm-button>
            </template>
            </div>
            <div class="opc-zm-base-card__collapse opc-zm-base-card__collapse--reset" v-if="!isExpanded && formPlan">
              {{
                $t('billing.zone.desc1', [
                  form.selectedNumber,
                  $money(formPlan ? formPlan.amount : 0),
                  $t(`billing.common.per_billing_cycle_user.${form.billingCycle}`),
                ])
              }}
            </div>
          </div>
        </div>
      </div>
      <div class="opc-zmone__main">
        <zm-form ref="zmoneForm" label-position="top" class="opc-config-form">
          <zm-form-item>
            <p class="opc-zmone__form-label" slot="label">
              {{ $t('billing.zone.select_license') }}
              <zm-tooltip class="item" aria-label="Learn more about Select the number of users" placement="top">
                <span class="opc-zone__tip" slot="content" v-html="$t('billing.zone.attendee_info_edit')"></span>
                <i class="zm-icon-info-outline main-icon"
                  @click="() => $tracking.buyInteraction(trackingPlanName + ' options', 'number of users - tooltip')"></i>
              </zm-tooltip>
            </p>
            <InputNumberWithPopover class="opc-zmone__quantity-selector" v-model="form.selectedNumber" size="large"
              controls-position="both" :min="minHost" :max="9999" @change="submitForm"
              :label="`${title} Select the number of users`" :decrease-tips="popUpDecreaseTips"/>
            <zm-alert class="reason-tips" v-if="licenseTips" type="info" :closable="false">
              <i class="zm-icon-info" style="color: #9191A4;"></i>
              <p @click='onLicenseTipsClick' v-html="licenseTips"></p>
            </zm-alert>
          </zm-form-item>
          <zm-form-item :label="$t('billing.select_billing_cycle')">
            <zm-radio-group
              v-model="form.billingCycle"
              class="opc-card-radio"
              :aria-label="`${title} Billing Cycle`"
              @change="submitForm"
            >
              <zm-radio v-for="plan in currentPlans" :key="plan.billing_cycle" :label="plan.billing_cycle"
                :disabled="billingCycleDisabled(plan)" class="opc-zmone__radio">
                <p class="opc-zmone__radio-title text-capitalize">{{ $t(`billing.billing_cycle.${plan.billing_cycle}`) }}
                  <save-count
                    v-if="plan.cycleValue >= BillingCycleEnum()[BILLING_CYCLE.QUARTERLY]"
                    :disabled="billingCycleDisabled(plan)"
                    :annual-price="(plan.amount / plan.cycleValue) * 12"
                    :monthly-price="cyclePrice.shortestCycleMonthlyPrice"
                  />
                </p>
                <p class="opc-zmone__radio-price" tabindex="-1">
                  <span>
                    {{ $money(plan.amount, { forceDiff: true }) }}
                  </span>
                  <span class="opc-zmone__radio-price-unit">{{ getPlanUnitWithCycle(plan.billing_cycle) }}</span>
                </p>
              </zm-radio>
            </zm-radio-group>
            <zm-alert class="reason-tips"
              v-if="form.billingCycle !== cycleDisabledKeyAndReason.key && cycleDisabledKeyAndReason.reason" type="info"
              :closable="false">
              <i class="zm-icon-info" style="color: #9191A4;"></i>
              <p @click="onCycleClick" v-html="cycleDisabledKeyAndReason.reason"></p>
            </zm-alert>
          </zm-form-item>
          <preview-payment />
        </zm-form>
      </div>
    </foldable>
  </tile>
</template>
<script>
import Foldable from '@/components/foldable.vue';
import Tile from '@/components/tile.vue';
import InputNumberWithPopover from '@/components/input-number/input-number-with-popover';
import logo from '@/assets/plans/icon_ZM.svg';
import {
  A_LINK_TAG_NAME,
  A_LINK_TEXT_CONTENT,
  BILLING_CYCLE, EDIT_FLOW_TRACKING_TYPE,
  limitBillingCycleWithBaseZoneTypes,
  planTypes,
  BASE_PLAN_SUB_TYPES,
  ZOOM_MEETING_PRO_MIN_LICENSE, BillingCycleEnum, AUDIO_SUB_TYPES,
  unlockBizLicenseCount
} from '@/common/constants';
import { ServiceConfirm } from '@/components/common/service-dialog/confirm';
import FullComparisonDialog from '@/plans/ZOOM_MEETING/dialog/full-comparison-dialog';
import PlanDetails from '@/plans/ZOOM_MEETING/dialog/plan-details';
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import _, { debounce, get, groupBy, throttle } from 'lodash';
import { getMeetingPlanByCategory, getCategoryNameI18nKey } from '@/plans/ZOOM_MEETING/common/utils';
import plansMixin from '@/plans/mx/plan';
import { isEmptyVal } from '@/utils';
import { getTitleNameI18nKey } from '../common/utils';
import saveMixins from '../save-mixins';
import { getPlanUnitWithCycle } from '@/common/product-unit';
import { validDownSellLicense } from '@/utils/validate-downsell';
import audioUsCaMixin from '@/common/mixin/audio-us-ca-mixin';
import { getMessageTemplateByRules } from '@/views/config/partner-refer/helper/validator';
import PreviewPayment from '@/views/checkout/payment/preview-payment';
import { flowTypes, pageConfig } from '@/common/service/page-config';

export default {
  mixins: [plansMixin, saveMixins, audioUsCaMixin],
  data() {
    return {
      logo,
      isExpanded: true,
      form: {
        category: 'pro',
        billingCycle: 'annual',
        selectedNumber: 1,
      },
      oldPlanCode: '',
      BILLING_CYCLE: BILLING_CYCLE,
      fullComparisonDialog: new ServiceConfirm(FullComparisonDialog, this.$store),
      planDetailsDialog: new ServiceConfirm(PlanDetails, this.$store),
      isInitUpdateForm: false,
      canExpand: true,
      showFoldable: true,
      alertTitle: ''
    };
  },
  computed: {
    ...mapState({
      planMapFlat: state => state.ShoppingCart.planMapFlat,
      boughtPlans: state => state.ShoppingCart.boughtPlans,
      assignInfo: state => state.ShoppingCart.assignInfo,
      updateBasePlan: state => state.Product.updateBasePlan,
    }),
    ...mapGetters(['getSelectedPlans', 'getUserCurrentSubscriptions', 'enableStandaloneCycleLimit', 'basicConfig', 'disableMonthly2AnnualTip', 'isMultipleCoupon', 'getCurrentBasePlanInfo', 'plansofBizBundle', 'limitLicenseWithBaseZoneTypes', 'partnerRefer', 'migratePlanState']),
    title() {
      return this.$t(getTitleNameI18nKey(this.form?.category))
    },
    zonePlans() {
      return this.planMapFlat.filter((item) => item.type === planTypes.ZOOM_MEETING);
    },
    zonePlanGroupByCategory() {
      return groupBy(this.zonePlans, 'category');
    },
    currentPlans() {
      return this.zonePlanGroupByCategory[this.form.category];
    },
    showPlanDetails() {
      return (
        this.$query.from === 'pro' && pageConfig?.editConfig?.planType === planTypes.ZOOM_MEETING && pageConfig.type === flowTypes.edit
      )
    },
    annualPriceM() {
      const monthPlan = this.currentPlans.find((item) => item.billing_cycle === BILLING_CYCLE.MONTHLY);
      return monthPlan ? monthPlan.amount * 12 : 0;
    },
    annalPriceSave() {
      const annualPlan = this.currentPlans.find((item) => item.billing_cycle === BILLING_CYCLE.ANNUAL);
      if (this.annualPriceM && annualPlan) {
        return this.annualPriceM - annualPlan.amount;
      }
      return 0;
    },
    saveLabel() {
      return {
        [BILLING_CYCLE.MONTHLY]: '',
        [BILLING_CYCLE.ANNUAL]: this.annalPriceSave > 0
          ? `${this.$money(this.annalPriceSave)} ${this.$t('billing.annual_save')}`
          : '',
      };
    },
    // current selected meeting plan
    formPlan() {
      return this.zonePlanGroupByCategory[this.form.category].find(
        (item) => item.billing_cycle === this.form.billingCycle,
      );
    },
    totalCoast() {
      if (this.formPlan) {
        return this.formPlan.amount * this.form.selectedNumber;
      }
      return 0;
    },
    limitCycleAddonPlans() {
      const disableCycle = this.currentPlans.find((p) => p.cycleValue < this.boughtMeetingPlan.cycleValue)?.cycleValue;
      if (!disableCycle) {
        return [];
      }

      const newLimit = this.enableStandaloneCycleLimit ? [planTypes.ZOOM_SCHEDULER] : [];
      return this.getUserCurrentSubscriptions.filter(item => (
        limitBillingCycleWithBaseZoneTypes.concat(newLimit).includes(item.type) &&
        item.cycleValue > disableCycle
      ));
    },
    maxLicenseAddon() {
      return this.getUserCurrentSubscriptions
        .filter(item => _.includes(this.limitLicenseWithBaseZoneTypes, item.type))
        .reduce((maxPlan, plan) => {
          if (plan.cartInfo.selected_number > get(maxPlan, 'cartInfo.selected_number', 0)) {
            return plan;
          }
          return maxPlan;
        }, {});
    },
    attendee() {
      return get(this.currentPlans, "[0].participants", 0);
    },
    trackingPlanName() {
      return this.$t(`Zoom One ${this.form?.category}`);
    },
    hasAccountDiscount() {
      return get(this.basicConfig, 'hasAccountDiscount', false);
    },
    boughtMeetingPlan() {
      return this.boughtPlans.find(item => item.type === planTypes.ZOOM_MEETING);
    },
    bizHasNotStandardPriceUnlimitedAudioPlan() {
      return this.form.category === BASE_PLAN_SUB_TYPES.BIZ
        && this.boughtPlans.some(item => item.type === planTypes.TOLLFREE && item.category === AUDIO_SUB_TYPES.UNLIMITED && item.isStandardPrice === false);
    },
    isStandardPrice() {
      if (this.hasAccountDiscount) {
        return true;
      }
      return this.boughtMeetingPlan.isStandardPrice && !this.bizHasNotStandardPriceUnlimitedAudioPlan;
    },
    zmUnitedPlanQuantity() {
      return this.boughtPlans
        .filter(item => item.type === planTypes.ZOOM_UNITED)
        .reduce((pre, item) => item.cartInfo.selected_number + pre, 0);
    },
    zoneBundlePlanQuantity() {
      return this.boughtPlans
        .filter(item => item.type === planTypes.ZOOM_ONE_BUNDLE)
        .reduce((pre, item) => item.cartInfo.selected_number + pre, 0);
    },
    minHostAndReason() {
      const boughtLicense = get(this.boughtMeetingPlan, 'cartInfo.selected_number', 1);
      if (this.basicConfig.downSellData && this.basicConfig.downSellData.disableDownSell) {
        const reason = this.$t('billing.error.can_not_bought_plan_tips');
        // MRR >= 500 (direct account)
        return {
          minHost: boughtLicense,
          reason,
          popUpReason: reason,
          trackingType: EDIT_FLOW_TRACKING_TYPE.NOT_ABLE_TO,
        };
      } else if (!this.isStandardPrice) {
        const reason = this.$t('billing.planform.meeting_license_custom_pricing');

        // customized price
        return {
          minHost: boughtLicense,
          reason,
          popUpReason: reason,
          trackingType: EDIT_FLOW_TRACKING_TYPE.HAS_CUSTOMER_PRICING,
        }
      }

      const meetingMin = get(this.formPlan, 'min', 1);
      const bundleLicense = this.zmUnitedPlanQuantity + this.zoneBundlePlanQuantity;
      let minHost = Math.max(meetingMin - bundleLicense, 1);

      const maxLicenseAddonLicense = get(this.maxLicenseAddon, 'cartInfo.selected_number', 0);

      if (this.maxLicenseAddon && minHost < maxLicenseAddonLicense - bundleLicense) {
        const maxLicenseAddonTypeNameKey = this.maxLicenseAddon.typeNameKey;
        const maxLicenseAddonTypeName = this.$t(maxLicenseAddonTypeNameKey);
        return {
          minHost: maxLicenseAddonLicense - bundleLicense,
          reason: this.$t('billing.meeting_license_decrease_tips_need_decrease_addon', { product: maxLicenseAddonTypeName }),
          popUpReason: this.$t('billing.licences_limit_tips', [maxLicenseAddonTypeName, this.$t('billing.planform.title.zoom_meeting_zoomonenew')]),
          trackingType: EDIT_FLOW_TRACKING_TYPE.KEEP_ADDON,
        }
      }
      // if edit biz and no biz plus plan, minHost >= ZOOM_MEETING_PRO_MIN_LICENSE
      const { plans } = this.getCurrentBasePlanInfo;
      if (plans?.[0]?.category === BASE_PLAN_SUB_TYPES.BIZ) {
        const { bundleLicenses } = this.plansofBizBundle;
        // only show when bundleLicenses > 0, which means user has been purchased biz+
        // if the current currency is not support biz+, we don't need to show this banner
        if (bundleLicenses > 0) {
          minHost = unlockBizLicenseCount ? 1 : Math.max(ZOOM_MEETING_PRO_MIN_LICENSE - this.zmUnitedPlanQuantity - bundleLicenses, 1);
          const reason = this.form.selectedNumber > 1 ? this.$t('billing.zobizplus.required_license_warning', [ZOOM_MEETING_PRO_MIN_LICENSE]) : '';
          return {
            minHost,
            reason,
            popUpReason: reason,
          }
        }
        // check biz plan
        const quantity = getMeetingPlanByCategory(this.boughtPlans)?.cartInfo?.selected_number ?? ZOOM_MEETING_PRO_MIN_LICENSE;
        minHost = quantity > ZOOM_MEETING_PRO_MIN_LICENSE ? ZOOM_MEETING_PRO_MIN_LICENSE : quantity;
      }
      // pro2biz
      if (this.migratePlanState.is.pro2biz) {
        minHost = Math.max(this.boughtMeetingPlan.cartInfo.selected_number, ZOOM_MEETING_PRO_MIN_LICENSE);
      }
      return {
        minHost: minHost,
        reason: '',
        popUpReason: '',
      };
    },
    minHost() {
      return this.minHostAndReason.minHost;
    },
    minHostReason() {
      return this.minHostAndReason.reason;
    },
    minHostPopUpReason() {
      return this.minHostAndReason.popUpReason;
    },
    cycleDisabledKeyAndReason() {
      const { isBulkLicenseAccount } = this.basicConfig;
      if (isBulkLicenseAccount) {
        return {
          key: this.form.billingCycle === BILLING_CYCLE.MONTHLY ? BILLING_CYCLE.ANNUAL : BILLING_CYCLE.MONTHLY,
          reason: this.$t('billing.error.buld_acount_license'),
        };
      }

      if (this.disableMonthly2AnnualTip && !this.isMultipleCoupon) {
        return {
          key: BILLING_CYCLE.ANNUAL,
          reason: this.disableMonthly2AnnualTip,
        }
      }

      if (this.cycleDisabledKeyAndReasonForPartner) {
        return this.cycleDisabledKeyAndReasonForPartner;
      }

      const planGroupByType = groupBy(this.limitCycleAddonPlans, 'type');
      const typeNames = Object.keys(planGroupByType).map(type => {
        const typeNameKey = get(planGroupByType[type], '0.typeNameKey', '');
        return this.$t(typeNameKey);
      }).filter(Boolean);
      if (this.limitCycleAddonPlans.length) {
        const disablePlan = this.currentPlans.find((p) => p.cycleValue < this.boughtMeetingPlan.cycleValue);
        return {
          key: disablePlan.billing_cycle,
          reason: this.$t('billing.planform.can_not_select_with_special_addon', [
            this.$t('billing.planform.title.zoom_meeting_zoomonenew'),
            typeNames.join(','),
            this.$t(`billing.billing_cycle.${disablePlan.billing_cycle}`)?.toLowerCase(),
          ]),
        };
      }
      return {};
    },
    canSwitchCategory() {
      const hasBundle = !!(this.zmUnitedPlanQuantity + this.zoneBundlePlanQuantity);
      return !hasBundle;
    },
    assignedMeetingInfo() {
      const boughtMeetingCode = get(this.boughtMeetingPlan, 'plan_code', '');
      return this.assignInfo[boughtMeetingCode];
    },
    needUnAssignTips() {
      return validDownSellLicense({ quantity: this.form.selectedNumber })?.errorMessage;
    },
    mx_decreaseTips() {
      if (this.needUnAssignTips) {
        return this.needUnAssignTips;
      }
      if (this.form.selectedNumber <= this.minHost) {

        if (this.$context.is.mixBuy) {
          return this.minHostPopUpReason;
        }

        return this.minHostReason;
      }
      return '';
    },
    popUpDecreaseTips() {
      if (this.$context.is.mixBuy) return this.mx_decreaseTips;
      return "";
    },
    licenseTips() {
      if (this.$context.is.mixBuy) return "";
      return this.mx_decreaseTips;
    },
    cartMeetingPlan() {
      return this.getSelectedPlans.find((item) => item.type === planTypes.ZOOM_MEETING);
    },
    cycleDisabledKeyAndReasonForPartner() {
      // MIX_BUY HARDCODE FOR SWITCH TO MONTHLY
      if (this.$context.is.mixBuy) {
        const message =  getMessageTemplateByRules(this.partnerRefer?.blockBasePlanSwitchToMonthlyMsg)?.messageBody;

        if (message) {
          return {
            key: BILLING_CYCLE.MONTHLY,
            reason: message,
          };
        }
      };

      return undefined;
    }
  },
  created() {
    this.updateForm(true);
  },
  mounted() {
    this.$context.$on('collapse-plan-card', this.collapseCard);
  },
  methods: {
    BillingCycleEnum() {
      return BillingCycleEnum
    },
    ...mapActions({
      switchSelectPlan: 'switchSelectPlan',
    }),
    ...mapMutations(['updateSelectPlan', 'savePersist', 'setUpdateBasePlan']),
    // sync shopping cart plan to local form state
    updateForm(isInitUpdateForm = false) {
      const shoppingCartPlan = this.getSelectedPlans.find((item) => item.type === planTypes.ZOOM_MEETING);
      if (shoppingCartPlan) {
        this.form.category = shoppingCartPlan.category;
        this.form.billingCycle = shoppingCartPlan.billing_cycle;
        this.form.selectedNumber = shoppingCartPlan.cartInfo.selected_number;
        this.oldPlanCode = shoppingCartPlan.plan_code;
      }

      this.isInitUpdateForm = isInitUpdateForm;
    },
    submitForm: throttle(function (v) {
      this.$nextTick(() => {
        if (!this.formPlan) {
          return;
        }

        if (isEmptyVal(v)) {
          this.form.selectedNumber = Math.max(this.formPlan?.cartInfo?.selected_number ?? 0, this.minHost);
        }

        if (this.formPlan.plan_code === this.oldPlanCode) {
          this.updateSelectPlan({
            ...this.formPlan,
            cartInfo: {
              selected_number: this.form.selectedNumber,
            },
          });
        } else {
          this.switchSelectPlan([
            this.oldPlanCode,
            this.formPlan.plan_code,
            {
              selected_number: this.form.selectedNumber,
            },
          ]);
        }
        this.updateForm();
      });
    }, 200),
    async handleExploreOtherPlansClick() {
      this.$tracking.buyInteraction('explore other plans', `${this.trackingPlanName} - explore other plans`);
      await this.showFullComparisonDialog();
    },
    async showFullComparisonDialog() {
      this.$tracking.buyInteraction('product details', 'full plan comparison - impression');
      await this.fullComparisonDialog.confirm();
      this.$nextTick(() => this.updateForm());
    },
    getCategoryName(category) {
      return this.$t(getCategoryNameI18nKey(category));
    },
    updateCollapsedPlan(from) {
      this.updateForm();
      this.alertTitle = this.$t('billing.zone.alert_title', [from, this.$t(this.boughtMeetingPlan?.planNameKey)])
      this.canExpand = false;
      this.isExpanded = false;
      this.setUpdateBasePlan({ update: false, from: '' });
      this.showFoldable = false;
      this.$nextTick(() => {
        this.showFoldable = true; // Avoid the appearance of the collapse animation.
      })
    },
    onLicenseTipsClick(event) {
      if (event?.target?.tagName === A_LINK_TAG_NAME && event?.target?.textContent === A_LINK_TEXT_CONTENT.FORM) {
        this.$tracking.buyInteraction(
          'banner - product level',
          `${this.trackingPlanName} - 'you are not able to make this change to  your account mid-contract' - contact form`);
        return;
      }
      this.doReturnPlanManagementTracking(event, 'adjust the add-on product and base product license quantity');
    },
    onCycleClick(event) {
      this.doReturnPlanManagementTracking(event, 'update to monthly subscription first');
    },
    doReturnPlanManagementTracking(event, msg) {
      if (event?.target?.tagName === A_LINK_TAG_NAME && event?.target?.textContent === A_LINK_TEXT_CONTENT.RETURN_PLAN_MANAGEMENT) {
        this.$tracking.buyInteraction(
          'banner - product level',
          `${this.trackingPlanName} - ${msg} - return to plan management`);
      }
    },
    getPlanUnitWithCycle(cycle) {
      return getPlanUnitWithCycle(cycle);
    },
    billingCycleDisabled({ billing_cycle, disabled }) {
      return this.cycleDisabledKeyAndReason.key === billing_cycle || disabled
    },
    collapseCard() {
      this.isExpanded = false;
    }
  },
  components: {
    Foldable,
    Tile,
    InputNumberWithPopover,
    PreviewPayment,
  },
  watch: {
    isExpanded: {
      handler(val) {
        this.$tracking.buyInteraction(
          'product details accordion',
          `${this.trackingPlanName} - ${val ? 'expand' : 'collapse'}`,
        );
      },
    },
    'form.billingCycle': {
      immediate: true,
      handler: debounce(function (val) {
        !this.isInitUpdateForm && this.$tracking.buyInteraction(
          'billing cycle',
          `${this.trackingPlanName} - toggled billed ${val}`,
        );
        if (val !== this.cycleDisabledKeyAndReason.key && this.cycleDisabledKeyAndReason.reason) {
          this.$tracking.buyInteraction(
            'banner - product level',
            `${this.trackingPlanName} - update to monthly subscription first - impression`);
        }
      }, 500),
    },
    'form.selectedNumber': {
      handler: debounce(function (newLicense, oldLicense) {
        let _newLicense = newLicense;
        if (this.$context.is.delta) _newLicense = this.deltaForm?.selectedNumber;
        if (this.formPlan) {
          // event tracking
          !this.isInitUpdateForm && this.$tracking.buyInteraction('number of hosts',
            `${this.trackingPlanName} - ${newLicense > oldLicense ? 'increase' : 'decrease'} - ${_newLicense}`);
        }
      }, 500),
    },
    updateBasePlan: {
      handler(val) {
        val?.update && this.updateCollapsedPlan(val?.from);
      },
    },
    licenseTips: {
      handler(val) {
        if (val) {
          if (this.form.selectedNumber <= this.minHost) {
            if (this.minHostAndReason?.trackingType) {
              switch (this.minHostAndReason.trackingType) {
                case EDIT_FLOW_TRACKING_TYPE.NOT_ABLE_TO:
                  this.$tracking.buyInteraction(
                    'banner - product level',
                    `${this.trackingPlanName} - you are not able to make this change to  your account mid-contract - impression`);
                  break;
                case EDIT_FLOW_TRACKING_TYPE.HAS_CUSTOMER_PRICING:
                  this.$tracking.buyInteraction(
                    'banner - product level',
                    `${this.trackingPlanName} - your account has custom pricing - impression`);
                  break;
                case EDIT_FLOW_TRACKING_TYPE.KEEP_ADDON:
                  this.$tracking.buyInteraction(
                    'banner - product level',
                    `${this.trackingPlanName} - adjust the add-on product and base product license quantity - impression`);
              }
            }
          }
        }
      },
    },
    cartMeetingPlan: {
      deep: true,
      handler() {
        this.updateForm();
      },
    }
  },
};
</script>

<style lang="scss">
@import '@/styles/_variables.scss';
@import '@/styles/zoom-card.scss';

.opc-zmone {
  .main-icon {
    color: $theme-color;
  }

  .reason-tips {
    margin-top: 12px;

    .zm-alert__description {
      display: flex;
      align-items: center;
    }

    p {
      margin-left: 13px;
      font-family: 'Almaden Sans';
      font-style: normal;
      font-weight: 500;
      font-size: 14px;
      line-height: 150%;
      color: #515079;
    }
  }

  &__link.zm-button--link {
    margin-left: 5px;
    font-weight: 700 !important;
    color: $theme-color;
    padding: 0;
  }

  &__quantity-selector {
    margin-top: 12px;
  }

  &__desc {
    padding-bottom: 20px;
    font-weight: 500;
    font-size: 14px;
    line-height: 22px;
    color: #00053d;
  }

  &__main {
    margin-top: 24px;
    padding-top: 24px;
    border-top: 1px solid #dfdfe4;
  }

  &__desc {
    position: relative;
    font-family: 'Almaden Sans';
    font-style: normal;
    font-weight: 500;
    font-size: 14px;
    line-height: 150%;
    color: #515079;
    padding: 0;
    margin: 0;

    &.is-warning {
      padding: 8px 12px 8px 40px;
      background: #FEF7D9;
      border-radius: 8px;

      &::before {
        content: '';
        background-image: url('../../../assets/Alert.svg');
        width: 16px;
        height: 16px;
        background-repeat: no-repeat;
        background-size: cover;
        position: absolute;
        top: calc(50% - 8px);
        left: 12px;
      }
    }
  }

  &__form-label {
    margin: 0;
    padding: 0;
    color: #00031F;
    font-size: 18px;
    font-style: normal;
    font-weight: 700;
    line-height: 27px;
  }

  .opc-card-radio {
    margin-top: 8px;

    .opc-zmone__radio {
      width: 100%;

      .zm-radio__wrap {
        width: 100%;
      }
    }
  }

  .opc-card-radio &__radio {
    .zm-radio__label--inner {
      width: 100%;
      // min-height: 136px;
      padding: 16px;
    }

    .zm-radio__label {
      &.is-disabled {
        .opc-zmone__radio-title, .opc-zmone__radio-price, .opc-zmone__radio-price-unit {
          color: #9191A4 !important;
        }
      }
    }

    &-title {
      color: #00053D;
      font-size: 16px;
      font-style: normal;
      font-weight: 600;
      line-height: 24px;
      margin: 0;
      display: flex;
      flex-wrap: wrap;

      .opc-price-save-count {
        margin-left: 4px;
      }
    }

    &-save-label {
      font-style: normal;
      font-weight: 600;
      font-size: 14px;
      line-height: 21px;
      color: #4a7d5c;
      min-width: 1px;
      min-height: 21px;
    }

    &-price {
      color: #00053D;
      font-size: 22px;
      font-style: normal;
      font-weight: 700;
      line-height: 33px;

      &-unit {
        color: #666487;
        font-size: 12px;
        font-style: normal;
        font-weight: 450;
        line-height: 120%;
      }

      &-delete {
        font-weight: 500;
        font-size: 14px;
        line-height: 150%;
        text-decoration-line: line-through;
        color: #515079;
      }
    }
  }
}
</style>

<style lang="scss">
.opc-zone__tip b {
  font-weight: bold !important;
}
</style>
